/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import {
  Focus,
  Goal,
  Plan,
  Topic,
  ActivityType,
  Verb,
} from "@ictlearningsciences/pal3-core/types";
import { useAppSelector } from "../store/hooks";
import { useWithLRStore } from "./useWithLRStore";
import { useWithRecommender } from "./useWithRecommender";
import { saveUserGoal } from "@ictlearningsciences/pal3-core/api";

export interface UseWithUserGoal {
  activeGoal: Goal | undefined;
  activeFocus: Focus | undefined;
  activeTopic: Topic | undefined;
  activePlan: Plan | undefined;
  setGoal: (goal: Goal) => void;
  setFocus: (goal: Goal, focus: Focus) => void;
  setTopic: (topic: Topic) => void;
  setPlan: (plan: Plan) => void;
}

export function useWithUserGoal(): UseWithUserGoal {
  const { addLearnerRecord } = useWithLRStore();
  const { getRecommendedTopic } = useWithRecommender();
  const { accessToken, activeGoal, activeFocus, activeTopic, activePlan } =
    useAppSelector((state) => state.userData);

  function setGoal(goal: Goal): void {
    if (!accessToken || goal.alias === activeGoal?.alias) return;
    const recommendedPlan = goal.plans[0]; // TODO: recommender for plans?
    const recommendedFocus = goal.focusList[0]; // TODO: recommender for focuses?
    const recommendedTopic = getRecommendedTopic(recommendedFocus);
    if (!recommendedTopic) {
      return;
    }
    addLearnerRecord({
      verb: Verb.SELECTED,
      objType: ActivityType.GOAL,
      objId: `${goal.id}+${recommendedFocus.id}+${recommendedTopic.id}+${recommendedPlan.id}`,
    });
    addLearnerRecord({
      verb: Verb.SELECTED,
      objType: ActivityType.TOPIC,
      objId: recommendedTopic.id,
    });
    saveUserGoal(
      accessToken,
      goal.id,
      recommendedFocus.id,
      recommendedTopic.id,
      recommendedPlan.id
    );
    setTopic(recommendedTopic);
  }

  function setFocus(goal: Goal, focus: Focus): void {
    if (!accessToken || focus.alias === activeFocus?.alias) return;
    const recommendedTopic = getRecommendedTopic(focus);
    const recommendedPlan = goal.plans[0]; // TODO: recommender for plans?
    if (!recommendedTopic) {
      return;
    }
    addLearnerRecord({
      verb: Verb.SELECTED,
      objType: ActivityType.GOAL,
      objId: `${goal.id}+${focus.id}+${recommendedTopic.id}+${recommendedPlan.id}`,
    });
    saveUserGoal(
      accessToken,
      goal.id,
      focus.id,
      recommendedTopic.id,
      recommendedPlan.id
    );
    setTopic(recommendedTopic);
  }

  function setTopic(topic: Topic): void {
    if (
      !accessToken ||
      !activeGoal ||
      !activeFocus ||
      topic.alias === activeTopic?.alias
    )
      return;
    addLearnerRecord({
      verb: Verb.SELECTED,
      objType: ActivityType.TOPIC,
      objId: topic.id,
    });
    saveUserGoal(
      accessToken,
      activeGoal.id,
      activeFocus.id,
      topic.id,
      activePlan?.id || ""
    );
  }

  function setPlan(plan: Plan): void {
    if (
      !accessToken ||
      !activeGoal ||
      !activeFocus ||
      plan.alias === activePlan?.alias
    )
      return;
    // TODO: need a separate activity type for plans
    addLearnerRecord({
      verb: Verb.SELECTED,
      objType: ActivityType.GOAL,
      objId: `${activeGoal?.id}+${activeFocus?.id}+${activeTopic?.id}+${plan.id}`,
    });
    saveUserGoal(
      accessToken,
      activeGoal.id,
      activeFocus.id,
      activeTopic?.id || "",
      plan.id
    );
  }

  return {
    activeGoal,
    activeFocus,
    activeTopic,
    activePlan,
    setGoal,
    setFocus,
    setTopic,
    setPlan,
  };
}
